import {CommentsCategory} from '@domain/comments'
import {PermissionType} from '@hconnect/apiclient'
import {FeatureFlag, FeatureFlagKey, checkFeatureFlag} from '@hproduce/feature-flags'
import type {DataScope, PermissionParsed} from '@hproduce/permissions'
import {checkMultiplePermissions, checkPermissionAccessByDataScope} from '@hproduce/permissions'

import {MainRoutes, mainRoutes, RouteName} from '../../routing'

type MainRouteName = keyof MainRoutes

// 1:1 mapping of a page to a Suite feature flag.  Not all feature flags map to pages (some control
// functionality within a page) but all pages map to a Suite feature flag.
const pageToFeatureMap: Record<MainRouteName, FeatureFlagKey> = {
  [RouteName.STOCK]: FeatureFlagKey.StorageLevels,
  [RouteName.PRODUCTION_PLANNING]: FeatureFlagKey.ProductionPlanning,
  [RouteName.SALES_FORECAST]: FeatureFlagKey.SalesForecast,
  [RouteName.ELECTRICITY_FORECAST]: FeatureFlagKey.ElectricityPriceForecast,
  [RouteName.OPTIMIZER]: FeatureFlagKey.Optimizer,
  [RouteName.OPERATOR_VIEW]: FeatureFlagKey.OperatorView
}

interface PermissionDetail {
  permissionTypes: [PermissionType, ...PermissionType[]]
  operator: 'AND' | 'OR'
}

export const mainPagesToPermissionsMap: Record<MainRouteName, PermissionDetail> = {
  [RouteName.STOCK]: {
    permissionTypes: ['VIEW_MATERIAL_STORAGE', 'VIEW_MACHINE_PLAN'],
    operator: 'AND'
  },
  [RouteName.ELECTRICITY_FORECAST]: {
    permissionTypes: ['VIEW_ELECTRICITY_PRICE'],
    operator: 'AND'
  },
  [RouteName.SALES_FORECAST]: {
    permissionTypes: ['VIEW_DEMAND'],
    operator: 'AND'
  },
  [RouteName.PRODUCTION_PLANNING]: {
    permissionTypes: ['CHANGE_MACHINE_PLAN', 'CHANGE_ELECTRICITY_ORDER'],
    operator: 'OR'
  },
  [RouteName.OPTIMIZER]: {
    permissionTypes: ['CHANGE_MACHINE_PLAN'],
    operator: 'AND'
  },
  [RouteName.OPERATOR_VIEW]: {
    permissionTypes: ['VIEW_MACHINE_PLAN'],
    operator: 'AND'
  }
}

export interface PageAvailabilityDetail {
  path: MainRoutes[MainRouteName]
  routeName: MainRouteName
  isAvailable: boolean
  hasPermission: boolean
}

export type GetPageAvailability = (
  featureFlags: FeatureFlag[],
  permissions: PermissionParsed[],
  countryId: string,
  plantCode: string
) => PageAvailabilityDetail[]

export const getPageAvailability: GetPageAvailability = (
  featureFlags,
  permissions,
  countryId,
  plantCode
) => {
  const isFeatureAvailable = (page: MainRouteName) =>
    checkFeatureFlag(featureFlags, pageToFeatureMap[page])

  const userHasPermission = (page: MainRouteName) =>
    checkIfMultiplePermissionsGranted({
      requiredPermissionTypes: mainPagesToPermissionsMap[page].permissionTypes,
      permissions,
      operator: mainPagesToPermissionsMap[page].operator,
      testFn: (permission) =>
        checkPermissionAccessByDataScope(countryId, plantCode, permission.parsedDataScope)
    })

  return Object.entries(mainRoutes).reduce<PageAvailabilityDetail[]>((acc, [pageName, path]) => {
    acc.push({
      path,
      routeName: pageName as MainRouteName,
      isAvailable: isFeatureAvailable(pageName as MainRouteName),
      hasPermission: userHasPermission(pageName as MainRouteName)
    })
    return acc
  }, [])
}

interface CheckIfMultiplePermissionsGrantedParams {
  permissions: PermissionParsed[]
  requiredPermissionTypes: PermissionType[]
  testFn: (permission: PermissionParsed) => boolean
  operator: 'OR' | 'AND'
}
/**
 * funtion to check if access is granted for multiple permission types
 * permission1 && permission2 or permission1 || permission2
 */
export const checkIfMultiplePermissionsGranted = ({
  requiredPermissionTypes,
  permissions,
  testFn,
  operator
}: CheckIfMultiplePermissionsGrantedParams) => {
  if (!permissions.length || !requiredPermissionTypes.length) {
    return false
  }

  if (operator === 'AND') {
    return checkMultiplePermissions({
      requiredPermissionTypes,
      permissions,
      testFn
    })
  }

  const isGranted = requiredPermissionTypes.some((permissionType) =>
    permissions.some(
      (permission) => permission.permissionType === permissionType && testFn(permission)
    )
  )

  return isGranted
}

/**
 * @deprecated this will be removed completely for all new roles and permissions, i.e. comments will be open to all
 */
export type PlannerDataScope = DataScope & {
  commentCategory?: CommentsCategory
}
